import { BaseApiClient } from '../client'
import type {
  Creator,
  CreateCreatorInput,
  UpdateCreatorInput,
  CommissionTier,
  CreatorSale,
  CreatorSaleSummary,
  CreatorDashboardData,
  PaginatedApiResult,
} from '../types'
import type { CreatorProgram, ConfigDiffEntry, TinyProduto } from '@/lib/types'

export type CreatorWithTier = Creator & {
  commissionTier: CommissionTier | null
  program?: CreatorProgram | null
  totalSalesAmount: number
  totalSalesCount: number
  totalCommission: number
}

export class CreatorsApi extends BaseApiClient {
  async getDashboard(filters?: {
    startDate?: string
    endDate?: string
    programId?: string
    tierId?: string
  }): Promise<CreatorDashboardData> {
    const params = new URLSearchParams()
    if (filters?.startDate) params.append('startDate', filters.startDate)
    if (filters?.endDate) params.append('endDate', filters.endDate)
    if (filters?.programId) params.append('programId', filters.programId)
    if (filters?.tierId) params.append('tierId', filters.tierId)

    const queryString = params.toString()
    return this.request<CreatorDashboardData>(
      `/api/admin/creators/dashboard${queryString ? `?${queryString}` : ''}`
    )
  }

  async list(
    page = 1,
    limit = 20,
    search?: string,
    status?: string,
    tierId?: string,
    sortBy?: string,
    sortDirection?: string,
    niche?: string,
    type?: string,
    programId?: string
  ) {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    })
    if (search) params.append('q', search)
    if (status && status !== 'all') params.append('status', status)
    if (tierId && tierId !== 'all') params.append('tierId', tierId)
    if (sortBy) params.append('sortBy', sortBy)
    if (sortDirection) params.append('sortDirection', sortDirection)
    if (niche && niche !== 'all') params.append('niche', niche)
    if (type && type !== 'all') params.append('type', type)
    if (programId && programId !== 'all') params.append('programId', programId)

    return this.requestPaginated<CreatorWithTier>(`/api/admin/creators?${params.toString()}`)
  }

  async listNiches() {
    return this.request<string[]>('/api/admin/creators/niches')
  }

  async get(id: string) {
    return this.request<CreatorWithTier>(`/api/admin/creators/${id}`)
  }

  async create(data: CreateCreatorInput) {
    return this.request<CreatorWithTier>('/api/admin/creators', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async update(id: string, data: UpdateCreatorInput): Promise<CreatorWithTier> {
    return this.request<CreatorWithTier>(`/api/admin/creators/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  async delete(id: string): Promise<void> {
    await this.request<void>(`/api/admin/creators/${id}`, { method: 'DELETE' })
  }

  async enrichInstagram(id: string) {
    return this.request<CreatorWithTier>(`/api/admin/creators/${id}/enrich-instagram`, {
      method: 'POST',
    })
  }

  async getSalesSummary(id: string): Promise<CreatorSaleSummary> {
    return this.request<CreatorSaleSummary>(`/api/admin/creators/${id}/sales/summary`)
  }

  async listSales(
    id: string,
    params?: { page?: number; limit?: number }
  ): Promise<PaginatedApiResult<CreatorSale>> {
    const searchParams = new URLSearchParams()
    if (params?.page) searchParams.set('page', params.page.toString())
    if (params?.limit) searchParams.set('limit', params.limit.toString())

    const query = searchParams.toString()
    return this.request<PaginatedApiResult<CreatorSale>>(
      `/api/admin/creators/${id}/sales${query ? `?${query}` : ''}`
    )
  }

  async syncCoupon(
    id: string
  ): Promise<CreatorWithTier & { errors: string[]; configDiff: ConfigDiffEntry[] }> {
    return this.request<CreatorWithTier & { errors: string[]; configDiff: ConfigDiffEntry[] }>(
      `/api/admin/creators/${id}/coupon/sync`,
      { method: 'POST' }
    )
  }

  async linkCoupon(
    id: string,
    syncToProgram = false
  ): Promise<{
    data: CreatorWithTier
    configDiff: ConfigDiffEntry[]
    warnings: string[]
  }> {
    const url = `${this.baseUrl}/api/admin/creators/${id}/coupon/link`
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ syncToProgram }),
    })
    const result = await response.json()
    if (!response.ok || result.error) {
      throw new Error(result.error?.message || 'Erro desconhecido')
    }
    return result
  }

  async deactivateCoupon(id: string): Promise<{ success: boolean; warnings: string[] }> {
    return this.request<{ success: boolean; warnings: string[] }>(
      `/api/admin/creators/${id}/coupon/deactivate`,
      { method: 'POST' }
    )
  }

  async bulkSeeding(data: { creatorIds: string[]; products: unknown[]; notes?: string }) {
    return this.request<{
      data: Array<{ creatorId: string; seeding: unknown; error: string | null }>
    }>('/api/admin/creators/bulk-seeding', { method: 'POST', body: JSON.stringify(data) })
  }

  async searchTinyProducts(query: string) {
    return this.request<TinyProduto[]>(
      `/api/admin/creators/tiny/products?q=${encodeURIComponent(query)}`
    )
  }
}
